import React, { useState, useEffect, useCallback } from 'react';
import { FaPlay, FaUndo, FaHistory, FaSearch, FaCalendarAlt, FaExclamationTriangle, FaCheckCircle, FaTimesCircle, FaTimes, FaChevronDown, FaChevronUp, FaRedo, FaExpand, FaSave } from 'react-icons/fa';
import '../../styles/MonthEndDuesManager.css';
import { authFetch } from '../../utils/authFetch';
import { supabase } from '../../utils/supabaseClient';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://krmmadhubanibackend.onrender.com';

const MONTH_NAMES = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

const MonthEndDuesManager = ({ show, onClose, onNotify }) => {
  const [activeTab, setActiveTab] = useState('run'); // 'run' | 'logs'
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [monthStatus, setMonthStatus] = useState(null); // null | { logs: [] }
  const [loadingStatus, setLoadingStatus] = useState(false);
  const [running, setRunning] = useState(false);
  const [reverting, setReverting] = useState(false);

  // Confirmation state
  const [confirmAction, setConfirmAction] = useState(null); // null | { type, title, message, onConfirm }

  // Expanded snapshot modal state
  const [expandedSnapshot, setExpandedSnapshot] = useState(null); // null | snapshot array
  const [expandedEdits, setExpandedEdits] = useState({}); // { index: { field: value } }
  const [expandedSearch, setExpandedSearch] = useState('');
  const [savingEdits, setSavingEdits] = useState(false);
  const [studentInfoMap, setStudentInfoMap] = useState({}); // { Rno: { Name, Class } }

  // Sorting state
  const [snapshotSort, setSnapshotSort] = useState({ field: 'Rno', direction: 'asc' }); // { field, direction: 'asc' | 'desc' }
  const [expandedSort, setExpandedSort] = useState({ field: 'Rno', direction: 'asc' });

  // Logs state
  const [logs, setLogs] = useState([]);
  const [loadingLogs, setLoadingLogs] = useState(false);
  const [expandedLogId, setExpandedLogId] = useState(null);
  const [snapshotSearch, setSnapshotSearch] = useState('');

  // Fetch status for selected month/year
  const fetchMonthStatus = useCallback(async () => {
    setLoadingStatus(true);
    try {
      const res = await authFetch(`${API_BASE_URL}/api/cron/month-end-dues-status?month=${selectedMonth}&year=${selectedYear}`);
      const data = await res.json();
      if (data.success) {
        setMonthStatus(data);
      }
    } catch (err) {
      console.error('Failed to fetch month status:', err);
    } finally {
      setLoadingStatus(false);
    }
  }, [selectedMonth, selectedYear]);

  // Fetch all logs
  const fetchLogs = useCallback(async () => {
    setLoadingLogs(true);
    try {
      const res = await authFetch(`${API_BASE_URL}/api/cron/logs?limit=50`);
      const data = await res.json();
      if (data.success) {
        setLogs(data.logs);
      }
    } catch (err) {
      console.error('Failed to fetch logs:', err);
    } finally {
      setLoadingLogs(false);
    }
  }, []);

  useEffect(() => {
    if (show) {
      fetchMonthStatus();
      fetchLogs();
    }
  }, [show, fetchMonthStatus, fetchLogs]);

  useEffect(() => {
    if (show) {
      fetchMonthStatus();
    }
  }, [selectedMonth, selectedYear, show, fetchMonthStatus]);

  // Fetch student info when snapshot data becomes available
  useEffect(() => {
    const snapshot = getLatestSuccessLog()?.metadata?.duesSnapshot;
    if (snapshot && snapshot.length > 0 && Object.keys(studentInfoMap).length === 0) {
      fetchStudentInfo(snapshot);
    }
  }, [monthStatus]);

  const getLatestSuccessLog = () => {
    if (!monthStatus?.logs) return null;
    return monthStatus.logs.find(l => l.status === 'success');
  };

  const getLatestRevertedLog = () => {
    if (!monthStatus?.logs) return null;
    return monthStatus.logs.find(l => l.status === 'reverted');
  };

  const getStatusBadge = () => {
    if (loadingStatus) return <span className="med-status-badge med-status-loading">Checking...</span>;
    const successLog = getLatestSuccessLog();
    const revertedLog = getLatestRevertedLog();
    
    if (successLog) {
      return <span className="med-status-badge med-status-success"><FaCheckCircle /> Completed</span>;
    }
    if (revertedLog) {
      return <span className="med-status-badge med-status-reverted"><FaUndo /> Reverted</span>;
    }
    return <span className="med-status-badge med-status-pending"><FaExclamationTriangle /> Not Run</span>;
  };

  // Manual Run handler
  const handleRunClick = async () => {
    await fetchMonthStatus();
    const successLog = getLatestSuccessLog();
    const revertedLog = getLatestRevertedLog();

    if (successLog) {
      setConfirmAction({
        type: 'force-run',
        title: 'Already Executed This Month',
        message: `Month-end dues for ${MONTH_NAMES[selectedMonth - 1]} ${selectedYear} was already processed on ${new Date(successLog.created_at).toLocaleString('en-IN')}.\n\nStudents updated: ${successLog.metadata?.duesUpdated || 0}\n\nRunning again will override the previous run and re-process all students. This may cause duplicate deductions if not reverted first.\n\nAre you absolutely sure you want to run again?`,
        danger: true,
        onConfirm: () => executeRun(true)
      });
    } else if (revertedLog) {
      setConfirmAction({
        type: 'run-after-revert',
        title: 'Previously Reverted',
        message: `Month-end dues for ${MONTH_NAMES[selectedMonth - 1]} ${selectedYear} was previously run but was reverted.\n\nDo you want to run the dues update again for this month?`,
        danger: false,
        onConfirm: () => executeRun(true)
      });
    } else {
      setConfirmAction({
        type: 'run',
        title: 'Confirm Manual Run',
        message: `This will update FeeDues for all active students who have not paid their monthly fee in ${MONTH_NAMES[selectedMonth - 1]} ${selectedYear}.\n\nStudents who already paid will be skipped.\n\nDo you want to proceed?`,
        danger: false,
        onConfirm: () => executeRun(false)
      });
    }
  };

  const executeRun = async (force) => {
    setConfirmAction(null);
    setRunning(true);
    try {
      const res = await authFetch(`${API_BASE_URL}/api/cron/run-month-end-dues`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ month: selectedMonth, year: selectedYear, force })
      });
      const data = await res.json();
      if (data.success) {
        const meta = data.log?.metadata;
        onNotify(`Month-end dues processed for ${MONTH_NAMES[selectedMonth - 1]} ${selectedYear}. Updated: ${meta?.duesUpdated || 0}, Skipped: ${meta?.skipped || 0}`, 'success');
        fetchMonthStatus();
        fetchLogs();
      } else {
        onNotify(data.error || 'Failed to run', 'error');
      }
    } catch (err) {
      onNotify('Server error: ' + err.message, 'error');
    } finally {
      setRunning(false);
    }
  };

  // Revert handler
  const handleRevertClick = async () => {
    await fetchMonthStatus();
    const successLog = getLatestSuccessLog();

    if (!successLog) {
      onNotify('No successful run found for this month to revert.', 'error');
      return;
    }

    const snapshot = successLog.metadata?.duesSnapshot;
    if (!snapshot || snapshot.length === 0) {
      onNotify('No snapshot data found in this log — nothing to revert.', 'error');
      return;
    }

    setConfirmAction({
      type: 'revert',
      title: 'Confirm Revert',
      message: `This will restore the previous FeeDues for ${snapshot.length} students who were updated in the ${MONTH_NAMES[selectedMonth - 1]} ${selectedYear} run.\n\nAll dues will be reverted to their values before the cron job ran.\n\nThis action cannot be undone. Are you sure?`,
      danger: true,
      onConfirm: () => executeRevert(successLog.id)
    });
  };

  const executeRevert = async (logId) => {
    setConfirmAction(null);
    setReverting(true);
    try {
      const res = await authFetch(`${API_BASE_URL}/api/cron/revert-month-end-dues`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ logId })
      });
      const data = await res.json();
      if (data.success) {
        onNotify(`Revert successful. Restored dues for ${data.revertedCount} students.`, 'success');
        fetchMonthStatus();
        fetchLogs();
      } else {
        onNotify(data.error || 'Revert failed', 'error');
      }
    } catch (err) {
      onNotify('Server error: ' + err.message, 'error');
    } finally {
      setReverting(false);
    }
  };

  // Get filtered and sorted snapshot
  const getFilteredSnapshot = (snapshot, search, sort = null) => {
    if (!snapshot) return [];
    const term = (search || snapshotSearch).trim().toLowerCase();
    let filtered = snapshot;
    if (term) {
      filtered = snapshot.filter(s => {
        const info = studentInfoMap[s.Rno];
        return (
          String(s.Rno).toLowerCase().includes(term) ||
          (info?.Name && info.Name.toLowerCase().includes(term)) ||
          (info?.Class && info.Class.toLowerCase().includes(term))
        );
      });
    }
    // Apply sorting
    const sortConfig = sort || snapshotSort;
    return filtered.sort((a, b) => {
      let aVal = a[sortConfig.field];
      let bVal = b[sortConfig.field];
      if (typeof aVal === 'string') aVal = aVal.toLowerCase();
      if (typeof bVal === 'string') bVal = bVal.toLowerCase();
      if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });
  };

  const handleSort = (field, isExpanded = false) => {
    const currentSort = isExpanded ? expandedSort : snapshotSort;
    const direction = currentSort.field === field && currentSort.direction === 'asc' ? 'desc' : 'asc';
    if (isExpanded) {
      setExpandedSort({ field, direction });
    } else {
      setSnapshotSort({ field, direction });
    }
  };

  const getSortIcon = (field, sort) => {
    if (sort.field !== field) return null;
    return sort.direction === 'asc' ? ' ↑' : ' ↓';
  };

  // Fetch student names and classes for snapshot Rnos
  const fetchStudentInfo = async (snapshot) => {
    if (!snapshot || snapshot.length === 0) return;
    const rnos = snapshot.map(s => s.Rno).filter(Boolean);
    try {
      const { data, error } = await supabase
        .from('students')
        .select('Rno, Name, Class')
        .in('Rno', rnos);
      if (!error && data) {
        const map = {};
        data.forEach(s => { map[s.Rno] = { Name: s.Name, Class: s.Class }; });
        setStudentInfoMap(map);
      }
    } catch (err) {
      console.error('Failed to fetch student info:', err);
    }
  };

  // Open expanded modal
  const openExpandedSnapshot = (snapshot) => {
    setExpandedSnapshot(snapshot);
    setExpandedEdits({});
    setExpandedSearch('');
    fetchStudentInfo(snapshot);
  };

  // Get edited value or original
  const getEditedValue = (idx, field, original) => {
    if (expandedEdits[idx] && expandedEdits[idx][field] !== undefined) return expandedEdits[idx][field];
    return original;
  };

  // Update an edit
  const updateEdit = (idx, field, value) => {
    setExpandedEdits(prev => ({
      ...prev,
      [idx]: { ...prev[idx], [field]: value }
    }));
  };

  // Save edits back to the snapshot in the log
  const saveExpandedEdits = async () => {
    if (!expandedSnapshot || Object.keys(expandedEdits).length === 0) return;
    setSavingEdits(true);
    try {
      // Apply edits to snapshot
      const updatedSnapshot = expandedSnapshot.map((s, idx) => {
        if (!expandedEdits[idx]) return s;
        const edits = expandedEdits[idx];
        const updated = { ...s };
        if (edits.prevDues !== undefined) updated.prevDues = parseFloat(edits.prevDues) || 0;
        if (edits.classFee !== undefined) updated.classFee = parseFloat(edits.classFee) || 0;
        if (edits.transportFee !== undefined) updated.transportFee = parseFloat(edits.transportFee) || 0;
        if (edits.examFee !== undefined) updated.examFee = parseFloat(edits.examFee) || 0;
        if (edits.defaultDiscount !== undefined) updated.defaultDiscount = parseFloat(edits.defaultDiscount) || 0;
        if (edits.newDues !== undefined) updated.newDues = parseFloat(edits.newDues) || 0;
        return updated;
      });

      // Save directly via Supabase
      const successLog = getLatestSuccessLog();
      if (successLog) {
        const { data: log, error: fetchErr } = await supabase
          .from('system_logs')
          .select('metadata')
          .eq('id', successLog.id)
          .single();

        if (fetchErr || !log) {
          onNotify('Log entry not found.', 'error');
          return;
        }

        const { error: updateErr } = await supabase
          .from('system_logs')
          .update({ metadata: { ...log.metadata, duesSnapshot: updatedSnapshot } })
          .eq('id', successLog.id);

        if (updateErr) {
          onNotify('Failed to save: ' + updateErr.message, 'error');
        } else {
          onNotify('Snapshot updated successfully.', 'success');
          setExpandedSnapshot(updatedSnapshot);
          setExpandedEdits({});
          fetchMonthStatus();
          fetchLogs();
        }
      }
    } catch (err) {
      onNotify('Server error: ' + err.message, 'error');
    } finally {
      setSavingEdits(false);
    }
  };

  const formatLogStatus = (status) => {
    const map = {
      success: { label: 'Success', className: 'med-log-success', icon: <FaCheckCircle /> },
      failed: { label: 'Failed', className: 'med-log-failed', icon: <FaTimesCircle /> },
      reverted: { label: 'Reverted', className: 'med-log-reverted', icon: <FaUndo /> },
      overridden: { label: 'Overridden', className: 'med-log-overridden', icon: <FaRedo /> },
      info: { label: 'Info', className: 'med-log-info', icon: <FaCheckCircle /> },
      warning: { label: 'Warning', className: 'med-log-warning', icon: <FaExclamationTriangle /> }
    };
    return map[status] || { label: status, className: '', icon: null };
  };

  if (!show) return null;

  const currentYear = new Date().getFullYear();
  const yearOptions = [currentYear - 1, currentYear, currentYear + 1];

  return (
    <div className="med-overlay" onClick={onClose}>
      <div className="med-modal" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="med-header">
          <div className="med-header-left">
            <FaCalendarAlt className="med-header-icon" />
            <h2>Month-End Dues Manager</h2>
          </div>
          <button className="med-close-btn" onClick={onClose}><FaTimes /></button>
        </div>

        {/* Tabs */}
        <div className="med-tabs">
          <button
            className={`med-tab ${activeTab === 'run' ? 'active' : ''}`}
            onClick={() => setActiveTab('run')}
          >
            <FaPlay /> Run & Manage
          </button>
          <button
            className={`med-tab ${activeTab === 'logs' ? 'active' : ''}`}
            onClick={() => { setActiveTab('logs'); fetchLogs(); }}
          >
            <FaHistory /> Execution Logs
          </button>
        </div>

        {/* Tab Content */}
        <div className="med-body">
          {activeTab === 'run' && (
            <div className="med-run-tab">
              {/* Month/Year Selector */}
              <div className="med-selector-row">
                <div className="med-select-group">
                  <label>Month</label>
                  <select value={selectedMonth} onChange={e => setSelectedMonth(parseInt(e.target.value))}>
                    {MONTH_NAMES.map((name, i) => (
                      <option key={i} value={i + 1}>{name}</option>
                    ))}
                  </select>
                </div>
                <div className="med-select-group">
                  <label>Year</label>
                  <select value={selectedYear} onChange={e => setSelectedYear(parseInt(e.target.value))}>
                    {yearOptions.map(y => (
                      <option key={y} value={y}>{y}</option>
                    ))}
                  </select>
                </div>
                <div className="med-status-display">
                  <label>Status</label>
                  {getStatusBadge()}
                </div>
              </div>

              {/* Info Card for selected month */}
              {monthStatus && !loadingStatus && (
                <div className="med-info-card">
                  {getLatestSuccessLog() ? (
                    <div className="med-info-success">
                      <FaCheckCircle />
                      <div>
                        <strong>Last run:</strong> {new Date(getLatestSuccessLog().created_at).toLocaleString('en-IN')}
                        <br />
                        <span>Trigger: {getLatestSuccessLog().metadata?.trigger || 'N/A'} | Updated: {getLatestSuccessLog().metadata?.duesUpdated || 0} | Skipped: {getLatestSuccessLog().metadata?.skipped || 0}</span>
                      </div>
                    </div>
                  ) : getLatestRevertedLog() ? (
                    <div className="med-info-reverted">
                      <FaUndo />
                      <div>
                        <strong>This month was reverted.</strong> The dues update was rolled back.
                        <br />
                        <span>You can run it again if needed.</span>
                      </div>
                    </div>
                  ) : (
                    <div className="med-info-pending">
                      <FaExclamationTriangle />
                      <div>
                        <strong>No run recorded for this month.</strong>
                        <br />
                        <span>You can trigger a manual run below.</span>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Action Buttons */}
              <div className="med-actions">
                <button
                  className="med-btn med-btn-run"
                  onClick={handleRunClick}
                  disabled={running || reverting || loadingStatus}
                >
                  {running ? (
                    <><span className="med-spinner"></span> Running...</>
                  ) : (
                    <><FaPlay /> Run Month-End Dues</>
                  )}
                </button>
                <button
                  className="med-btn med-btn-revert"
                  onClick={handleRevertClick}
                  disabled={running || reverting || loadingStatus || !getLatestSuccessLog()}
                >
                  {reverting ? (
                    <><span className="med-spinner"></span> Reverting...</>
                  ) : (
                    <><FaUndo /> Revert Last Run</>
                  )}
                </button>
              </div>

              {/* Snapshot Preview if success log exists */}
              {getLatestSuccessLog()?.metadata?.duesSnapshot?.length > 0 && (
                <div className="med-snapshot-section">
                  <div className="med-snapshot-header">
                    <h3>Dues Snapshot — {MONTH_NAMES[selectedMonth - 1]} {selectedYear}</h3>
                    <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                      <div className="med-snapshot-search">
                        <FaSearch />
                        <input
                          type="text"
                          placeholder="Search by Roll No, Name, Class..."
                          value={snapshotSearch}
                          onChange={e => setSnapshotSearch(e.target.value)}
                        />
                      </div>
                      <button
                        className="med-btn" title="Expand table"
                        style={{ padding: '6px 12px', fontSize: '0.85rem', background: '#6366f1', color: '#fff', border: 'none', borderRadius: '6px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '4px' }}
                        onClick={() => openExpandedSnapshot(getLatestSuccessLog().metadata.duesSnapshot)}
                      >
                        <FaExpand /> Expand
                      </button>
                    </div>
                  </div>
                  <div className="med-snapshot-table-wrap">
                    <table className="med-snapshot-table">
                      <thead>
                        <tr>
                          <th onClick={() => handleSort('Rno')} style={{ cursor: 'pointer', userSelect: 'none' }}>Roll No{getSortIcon('Rno', snapshotSort)}</th>
                          <th style={{ userSelect: 'none' }}>Name</th>
                          <th style={{ userSelect: 'none' }}>Class</th>
                          <th onClick={() => handleSort('prevDues')} style={{ cursor: 'pointer', userSelect: 'none' }}>Previous Dues{getSortIcon('prevDues', snapshotSort)}</th>
                          <th onClick={() => handleSort('classFee')} style={{ cursor: 'pointer', userSelect: 'none' }}>Class Fee{getSortIcon('classFee', snapshotSort)}</th>
                          <th onClick={() => handleSort('transportFee')} style={{ cursor: 'pointer', userSelect: 'none' }}>Transport Fee{getSortIcon('transportFee', snapshotSort)}</th>
                          <th onClick={() => handleSort('examFee')} style={{ cursor: 'pointer', userSelect: 'none' }}>Exam Fee{getSortIcon('examFee', snapshotSort)}</th>
                          <th onClick={() => handleSort('defaultDiscount')} style={{ cursor: 'pointer', userSelect: 'none' }}>Discount{getSortIcon('defaultDiscount', snapshotSort)}</th>
                          <th onClick={() => handleSort('newDues')} style={{ cursor: 'pointer', userSelect: 'none' }}>New Dues{getSortIcon('newDues', snapshotSort)}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {getFilteredSnapshot(getLatestSuccessLog().metadata.duesSnapshot, snapshotSearch).map((s, i) => (
                          <tr key={i}>
                            <td className="med-snap-rno">{s.Rno}</td>
                            <td style={{ fontWeight: 500 }}>{studentInfoMap[s.Rno]?.Name || '—'}</td>
                            <td>{studentInfoMap[s.Rno]?.Class || '—'}</td>
                            <td className={s.prevDues < 0 ? 'med-snap-negative' : 'med-snap-positive'}>
                              ₹{Math.abs(s.prevDues)}
                              {s.prevDues < 0 ? ' (Due)' : s.prevDues > 0 ? ' (Advance)' : ''}
                            </td>
                            <td>₹{s.classFee}</td>
                            <td>
                              {s.transportEnabled === false || (s.transportEnabled === undefined && !s.transportFee)
                                ? <span className="med-snap-muted">—</span>
                                : <span className="med-snap-tf-cell">
                                    <span className="med-snap-tf-amount">₹{s.transportFee || 0}</span>
                                    {s.transportFeeSource === 'student' && (
                                      <span className="med-snap-badge med-snap-badge-student" title="Custom transport fee set on this student's record">Student</span>
                                    )}
                                    {s.transportFeeSource === 'class' && (
                                      <span className="med-snap-badge med-snap-badge-class" title="Default transport fee from the classes table (student record had 0)">Class</span>
                                    )}
                                  </span>}
                            </td>
                            <td>
                              {s.examFee
                                ? <span>₹{s.examFee}</span>
                                : <span className="med-snap-muted">—</span>}
                            </td>
                            <td>
                              {s.defaultDiscount
                                ? <span className="med-snap-positive">−₹{s.defaultDiscount}</span>
                                : <span className="med-snap-muted">—</span>}
                            </td>
                            <td className={s.newDues < 0 ? 'med-snap-negative' : 'med-snap-positive'}>
                              ₹{Math.abs(s.newDues)}
                              {s.newDues < 0 ? ' (Due)' : s.newDues > 0 ? ' (Advance)' : ''}
                            </td>
                          </tr>
                        ))}
                        {getFilteredSnapshot(getLatestSuccessLog().metadata.duesSnapshot, snapshotSearch).length === 0 && (
                          <tr><td colSpan="9" className="med-no-data">No matching students</td></tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          )}

          {activeTab === 'logs' && (
            <div className="med-logs-tab">
              {loadingLogs ? (
                <div className="med-loading"><span className="med-spinner"></span> Loading logs...</div>
              ) : logs.length === 0 ? (
                <div className="med-no-data">No cron job logs found.</div>
              ) : (
                <div className="med-logs-list">
                  {logs.map(log => {
                    const statusInfo = formatLogStatus(log.status);
                    const isExpanded = expandedLogId === log.id;
                    const snapshot = log.metadata?.duesSnapshot;

                    return (
                      <div key={log.id} className={`med-log-item ${statusInfo.className}`}>
                        <div
                          className="med-log-header"
                          onClick={() => {
                            setExpandedLogId(isExpanded ? null : log.id);
                            if (!isExpanded && snapshot && snapshot.length > 0) {
                              fetchStudentInfo(snapshot);
                            }
                          }}
                        >
                          <div className="med-log-left">
                            {statusInfo.icon}
                            <div className="med-log-info">
                              <span className="med-log-event">
                                {log.event === 'month_end_dues_revert' ? 'Revert' : 'Month-End Dues'}
                              </span>
                              <span className="med-log-meta">
                                {log.metadata?.monthLabel || `${MONTH_NAMES[(log.metadata?.month || 1) - 1]} ${log.metadata?.year || ''}`}
                                {' • '}
                                {log.metadata?.trigger || log.event}
                              </span>
                            </div>
                          </div>
                          <div className="med-log-right">
                            <span className={`med-log-status-badge ${statusInfo.className}`}>
                              {statusInfo.label}
                            </span>
                            <span className="med-log-date">
                              {new Date(log.created_at).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' })}
                            </span>
                            {snapshot && snapshot.length > 0 && (
                              isExpanded ? <FaChevronUp className="med-log-toggle" /> : <FaChevronDown className="med-log-toggle" />
                            )}
                          </div>
                        </div>

                        {/* Log details */}
                        <div className="med-log-details">
                          {log.metadata?.duesUpdated !== undefined && (
                            <span>Updated: <strong>{log.metadata.duesUpdated}</strong></span>
                          )}
                          {log.metadata?.skipped !== undefined && (
                            <span>Skipped: <strong>{log.metadata.skipped}</strong></span>
                          )}
                          {log.metadata?.alreadyPaid !== undefined && (
                            <span>Already Paid: <strong>{log.metadata.alreadyPaid}</strong></span>
                          )}
                          {log.metadata?.errors !== undefined && log.metadata.errors > 0 && (
                            <span className="med-detail-error">Errors: <strong>{log.metadata.errors}</strong></span>
                          )}
                          {log.metadata?.revertedCount !== undefined && (
                            <span>Reverted: <strong>{log.metadata.revertedCount}</strong></span>
                          )}
                          {log.metadata?.durationMs !== undefined && (
                            <span>Duration: <strong>{log.metadata.durationMs}ms</strong></span>
                          )}
                        </div>

                        {/* Expanded snapshot */}
                        {isExpanded && snapshot && snapshot.length > 0 && (
                          <div className="med-log-snapshot">
                            <div className="med-snapshot-search med-log-snapshot-search">
                              <FaSearch />
                              <input
                                type="text"
                                placeholder="Search by Roll No, Name, Class..."
                                value={snapshotSearch}
                                onChange={e => setSnapshotSearch(e.target.value)}
                              />
                            </div>
                            <div className="med-snapshot-table-wrap">
                              <table className="med-snapshot-table">
                                <thead>
                                  <tr>
                                    <th>Roll No</th>
                                    <th>Name</th>
                                    <th>Class</th>
                                    <th>Previous Dues</th>
                                    <th>Class Fee</th>
                                    <th>Transport Fee</th>
                                    <th>Exam Fee</th>
                                    <th>Discount</th>
                                    <th>New Dues</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {getFilteredSnapshot(snapshot).map((s, i) => (
                                    <tr key={i}>
                                      <td className="med-snap-rno">{s.Rno}</td>
                                      <td style={{ fontWeight: 500 }}>{studentInfoMap[s.Rno]?.Name || '—'}</td>
                                      <td>{studentInfoMap[s.Rno]?.Class || '—'}</td>
                                      <td className={s.prevDues < 0 ? 'med-snap-negative' : 'med-snap-positive'}>
                                        ₹{Math.abs(s.prevDues)}
                                        {s.prevDues < 0 ? ' (Due)' : s.prevDues > 0 ? ' (Advance)' : ''}
                                      </td>
                                      <td>₹{s.classFee}</td>
                                      <td>
                                        {s.transportEnabled === false || (s.transportEnabled === undefined && !s.transportFee)
                                          ? <span className="med-snap-muted">—</span>
                                          : <span className="med-snap-tf-cell">
                                              <span className="med-snap-tf-amount">₹{s.transportFee || 0}</span>
                                              {s.transportFeeSource === 'student' && (
                                                <span className="med-snap-badge med-snap-badge-student" title="Custom transport fee set on this student's record">Student</span>
                                              )}
                                              {s.transportFeeSource === 'class' && (
                                                <span className="med-snap-badge med-snap-badge-class" title="Default transport fee from the classes table (student record had 0)">Class</span>
                                              )}
                                            </span>}
                                      </td>
                                      <td>
                                        {s.examFee
                                          ? <span>₹{s.examFee}</span>
                                          : <span className="med-snap-muted">—</span>}
                                      </td>
                                      <td>
                                        {s.defaultDiscount
                                          ? <span className="med-snap-positive">−₹{s.defaultDiscount}</span>
                                          : <span className="med-snap-muted">—</span>}
                                      </td>
                                      <td className={s.newDues < 0 ? 'med-snap-negative' : 'med-snap-positive'}>
                                        ₹{Math.abs(s.newDues)}
                                        {s.newDues < 0 ? ' (Due)' : s.newDues > 0 ? ' (Advance)' : ''}
                                      </td>
                                    </tr>
                                  ))}
                                  {getFilteredSnapshot(snapshot).length === 0 && (
                                    <tr><td colSpan="9" className="med-no-data">No matching students</td></tr>
                                  )}
                                </tbody>
                              </table>
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Expanded Snapshot Modal */}
        {expandedSnapshot && (
          <div className="med-confirm-overlay" onClick={() => setExpandedSnapshot(null)}>
            <div className="med-confirm-modal" onClick={e => e.stopPropagation()} style={{ maxWidth: '95vw', width: '95vw', maxHeight: '90vh', display: 'flex', flexDirection: 'column' }}>
              <div className="med-confirm-header med-confirm-normal" style={{ justifyContent: 'space-between', display: 'flex', alignItems: 'center' }}>
                <h3 style={{ margin: 0, display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <FaCalendarAlt /> Dues Snapshot — {MONTH_NAMES[selectedMonth - 1]} {selectedYear}
                  <span style={{ fontSize: '0.8rem', fontWeight: '400', color: '#6b7280' }}>({expandedSnapshot.length} students)</span>
                </h3>
                <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                  <div className="med-snapshot-search" style={{ margin: 0 }}>
                    <FaSearch />
                    <input type="text" placeholder="Search by Roll No, Name, Class..." value={expandedSearch} onChange={e => setExpandedSearch(e.target.value)} />
                  </div>
                  {Object.keys(expandedEdits).length > 0 && (
                    <button
                      className="med-btn med-btn-run" style={{ padding: '6px 14px', fontSize: '0.85rem' }}
                      onClick={saveExpandedEdits} disabled={savingEdits}
                    >
                      {savingEdits ? <><span className="med-spinner"></span> Saving...</> : <><FaSave /> Save Changes</>}
                    </button>
                  )}
                  <button className="med-close-btn" onClick={() => setExpandedSnapshot(null)} style={{ position: 'static' }}><FaTimes /></button>
                </div>
              </div>
              <div style={{ flex: 1, overflow: 'auto', padding: '16px' }}>
                <table className="med-snapshot-table" style={{ width: '100%' }}>
                  <thead>
                    <tr>
                      <th onClick={() => handleSort('Rno', true)} style={{ cursor: 'pointer', userSelect: 'none' }}>Roll No{getSortIcon('Rno', expandedSort)}</th>
                      <th style={{ userSelect: 'none' }}>Student Name</th>
                      <th style={{ userSelect: 'none' }}>Class</th>
                      <th onClick={() => handleSort('prevDues', true)} style={{ cursor: 'pointer', userSelect: 'none' }}>Previous Dues{getSortIcon('prevDues', expandedSort)}</th>
                      <th onClick={() => handleSort('classFee', true)} style={{ cursor: 'pointer', userSelect: 'none' }}>Class Fee{getSortIcon('classFee', expandedSort)}</th>
                      <th onClick={() => handleSort('transportFee', true)} style={{ cursor: 'pointer', userSelect: 'none' }}>Transport Fee{getSortIcon('transportFee', expandedSort)}</th>
                      <th onClick={() => handleSort('examFee', true)} style={{ cursor: 'pointer', userSelect: 'none' }}>Exam Fee{getSortIcon('examFee', expandedSort)}</th>
                      <th onClick={() => handleSort('defaultDiscount', true)} style={{ cursor: 'pointer', userSelect: 'none' }}>Discount{getSortIcon('defaultDiscount', expandedSort)}</th>
                      <th onClick={() => handleSort('newDues', true)} style={{ cursor: 'pointer', userSelect: 'none' }}>New Dues{getSortIcon('newDues', expandedSort)}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {getFilteredSnapshot(expandedSnapshot, expandedSearch, expandedSort).map((s, idx) => {
                      const origIdx = expandedSnapshot.indexOf(s);
                      const isEdited = !!expandedEdits[origIdx];
                      return (
                        <tr key={origIdx} style={isEdited ? { backgroundColor: '#fefce8' } : {}}>
                          <td className="med-snap-rno">{s.Rno}</td>
                          <td style={{ fontWeight: 500 }}>{studentInfoMap[s.Rno]?.Name || '—'}</td>
                          <td>{studentInfoMap[s.Rno]?.Class || '—'}</td>
                          <td>
                            <input type="number" className="med-edit-input"
                              value={getEditedValue(origIdx, 'prevDues', s.prevDues)}
                              onChange={e => updateEdit(origIdx, 'prevDues', e.target.value)}
                              style={{ width: '90px' }} />
                          </td>
                          <td>
                            <input type="number" className="med-edit-input"
                              value={getEditedValue(origIdx, 'classFee', s.classFee)}
                              onChange={e => updateEdit(origIdx, 'classFee', e.target.value)}
                              style={{ width: '80px' }} />
                          </td>
                          <td>
                            <input type="number" className="med-edit-input"
                              value={getEditedValue(origIdx, 'transportFee', s.transportFee || 0)}
                              onChange={e => updateEdit(origIdx, 'transportFee', e.target.value)}
                              style={{ width: '80px' }} />
                          </td>
                          <td>
                            <input type="number" className="med-edit-input"
                              value={getEditedValue(origIdx, 'examFee', s.examFee || 0)}
                              onChange={e => updateEdit(origIdx, 'examFee', e.target.value)}
                              style={{ width: '80px' }} />
                          </td>
                          <td>
                            <input type="number" className="med-edit-input"
                              value={getEditedValue(origIdx, 'defaultDiscount', s.defaultDiscount || 0)}
                              onChange={e => updateEdit(origIdx, 'defaultDiscount', e.target.value)}
                              style={{ width: '80px' }} />
                          </td>
                          <td>
                            <input type="number" className="med-edit-input"
                              value={getEditedValue(origIdx, 'newDues', s.newDues)}
                              onChange={e => updateEdit(origIdx, 'newDues', e.target.value)}
                              style={{ width: '90px' }} />
                          </td>
                        </tr>
                      );
                    })}
                    {getFilteredSnapshot(expandedSnapshot, expandedSearch).length === 0 && (
                      <tr><td colSpan="9" className="med-no-data">No matching students</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {/* Confirmation Modal */}
        {confirmAction && (
          <div className="med-confirm-overlay" onClick={() => setConfirmAction(null)}>
            <div className="med-confirm-modal" onClick={e => e.stopPropagation()}>
              <div className={`med-confirm-header ${confirmAction.danger ? 'med-confirm-danger' : 'med-confirm-normal'}`}>
                <FaExclamationTriangle />
                <h3>{confirmAction.title}</h3>
              </div>
              <div className="med-confirm-body">
                <p>{confirmAction.message}</p>
              </div>
              <div className="med-confirm-footer">
                <button className="med-btn med-btn-cancel" onClick={() => setConfirmAction(null)}>
                  Cancel
                </button>
                <button
                  className={`med-btn ${confirmAction.danger ? 'med-btn-danger' : 'med-btn-confirm'}`}
                  onClick={confirmAction.onConfirm}
                >
                  {confirmAction.danger ? 'Yes, I Understand' : 'Yes, Proceed'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default MonthEndDuesManager;
